import { Grid2 } from '@mui/material';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Common, OperationAdminControl } from '@components/Admin';

import * as ROUTES from '@constants/routes';

import { usePageContext } from '@contexts/page-context';

import { formatDate } from '@utils/format';

import {
  useAdminGetOperation,
  useAdminUpdateOperation,
} from 'generated/admin.generated';

interface OperationDrilldownProps {
  operation_id: string;
}

const messages = defineMessages({
  name: {
    id: 'admin.operations.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.operations.description',
    defaultMessage: 'Description',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  policies: {
    id: 'admin.operations.associated.policies',
    defaultMessage: 'Policies',
  },
});

interface FormValues {
  operation: string;
}

const OperationDrilldown = ({
  operation_id,
}: React.PropsWithChildren<OperationDrilldownProps>) => {
  const { data, loading: operationLoading } = useAdminGetOperation({
    variables: { input: { operation: operation_id } },
  });
  const { formatMessage } = useIntl();
  const { setTitle } = usePageContext();
  const [updateOperation, { loading: updateOperationLoading }] =
    useAdminUpdateOperation();

  const loading = updateOperationLoading || operationLoading;

  const operation = data?.getOperation;

  React.useEffect(() => {
    if (operation?.operation) {
      setTitle(operation.operation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.operation]);

  const initialValues: FormValues = {
    operation: data?.getOperation.operation ?? '',
  };

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    operation: Yup.string()
      .max(50)
      .matches(/^[a-z][a-zA-Z]*$/)
      .required(formatMessage({ id: 'GLOBAL.required' })),
  });

  const handleSubmit = async (values: FormValues) => {
    if (!operation) {
      return;
    }
    const response = await updateOperation({
      variables: {
        input: {
          operation: operation.operation,
          operationName: values.operation,
        },
      },
    });
    if (response.data?.updateOperation) {
      navigate(
        `${ROUTES.ADMIN_MANAGE_OPERATIONS}${response.data.updateOperation.operation}`,
        { replace: true }
      );
    }
  };

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 12, sm: 6 }}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {() => {
            const cardData: Common.Card.IMetadata.Metadata[] = [
              {
                title: formatMessage(messages.name),
                value: operation?.operation,
                editOptions: {
                  name: 'operation',
                  type: 'text',
                },
              },
              {
                title: formatMessage(messages.createdAt),
                value: formatDate({
                  date: operation?.created_at,
                  formatter: 'datetime',
                }),
              },
              {
                title: formatMessage(messages.updatedAt),
                value: formatDate({
                  date: operation?.updated_at,
                  formatter: 'datetime',
                }),
              },
            ];
            return (
              <Form>
                <Common.Card.Metadata loading={loading} data={cardData} />;
              </Form>
            );
          }}
        </Formik>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6 }}>
        <OperationAdminControl.PolicyEditor
          operation={operation}
          loading={loading}
        />
      </Grid2>
    </Grid2>
  );
};

export default OperationDrilldown;
